'use client';

import {
  formatBrowserUploadBytes,
  useBrowserFileUploads,
} from '@/hooks/uploads/use-browser-file-uploads';
import {
  CONTACT_ATTACHMENT_ACCEPTED_LABELS,
  CONTACT_ATTACHMENT_ACCEPTED_TYPES,
  CONTACT_ATTACHMENT_MAX_BYTES,
  CONTACT_ATTACHMENT_MAX_COUNT,
} from '@/lib/support/contact';
import { toast } from '@/lib/ui/toast';
import { uploadFileFromBrowser } from '@/storage/client';
import {
  type ChangeEvent,
  type ClipboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const CONTACT_ATTACHMENT_UPLOAD_FOLDER = 'ai/input/support/contact';

export type ContactFormAttachmentUploadStatus =
  | 'uploading'
  | 'uploaded'
  | 'failed';

export type ContactFormAttachment = {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  key?: string;
  previewUrl: string;
  uploadStatus: ContactFormAttachmentUploadStatus;
};

type ContactFormTranslator = (
  key: string,
  values?: Record<string, number | string>
) => string;

const createAttachmentId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

type UseContactAttachmentsParams = {
  t: ContactFormTranslator;
};

export function useContactAttachments({ t }: UseContactAttachmentsParams) {
  const [attachments, setAttachments] = useState<ContactFormAttachment[]>([]);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentsRef = useRef<ContactFormAttachment[]>([]);

  const acceptedFormats = useMemo(
    () => CONTACT_ATTACHMENT_ACCEPTED_LABELS.join('/'),
    []
  );
  const maxSizeLabel = useMemo(
    () => formatBrowserUploadBytes(CONTACT_ATTACHMENT_MAX_BYTES),
    []
  );
  const attachmentsHint = useMemo(
    () =>
      t('attachmentsHint', {
        formats: acceptedFormats,
        maxCount: CONTACT_ATTACHMENT_MAX_COUNT,
        maxSize: maxSizeLabel,
      }),
    [acceptedFormats, maxSizeLabel, t]
  );

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const { uploadAcceptedFile, validateFiles } = useBrowserFileUploads({
    acceptedTypes: CONTACT_ATTACHMENT_ACCEPTED_TYPES,
    maxBytes: CONTACT_ATTACHMENT_MAX_BYTES,
    maxCount: CONTACT_ATTACHMENT_MAX_COUNT,
    uploadFile: useCallback(
      (file: File) =>
        uploadFileFromBrowser(file, CONTACT_ATTACHMENT_UPLOAD_FOLDER),
      []
    ),
  });

  const addAttachments = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      const { accepted, rejected } = validateFiles(files, {
        currentCount: attachments.length,
      });

      const reportedReasons = new Set<string>();
      for (const rejectedFile of rejected) {
        if (rejectedFile.reason === 'invalid-type') {
          toast.error(t('attachmentTypeInvalid'));
          continue;
        }
        if (rejectedFile.reason === 'too-large') {
          toast.error(t('attachmentTooLarge', { maxSize: maxSizeLabel }));
          continue;
        }

        if (!reportedReasons.has('too-many')) {
          reportedReasons.add('too-many');
          toast.error(
            t('attachmentTooMany', { maxCount: CONTACT_ATTACHMENT_MAX_COUNT })
          );
        }
      }

      const nextAttachments: Array<{
        file: File;
        attachment: ContactFormAttachment;
      }> = [];
      for (const file of accepted) {
        nextAttachments.push({
          file,
          attachment: {
            id: createAttachmentId(),
            name: file.name || `image-${Date.now()}.png`,
            type: file.type,
            size: file.size,
            previewUrl: URL.createObjectURL(file),
            uploadStatus: 'uploading',
          },
        });
      }

      if (nextAttachments.length) {
        setAttachments((prev) => [
          ...prev,
          ...nextAttachments.map((item) => item.attachment),
        ]);

        await Promise.all(
          nextAttachments.map(async ({ file, attachment }) => {
            const result = await uploadAcceptedFile(file);
            if (result.status === 'uploaded') {
              setAttachments((prev) =>
                prev.map((item) =>
                  item.id === attachment.id
                    ? {
                        ...item,
                        url: result.upload.url,
                        key: result.upload.key,
                        uploadStatus: 'uploaded',
                      }
                    : item
                )
              );
              return;
            }

            setAttachments((prev) =>
              prev.map((item) =>
                item.id === attachment.id
                  ? {
                      ...item,
                      uploadStatus: 'failed',
                    }
                  : item
              )
            );
            toast.error(t('attachmentUploadFailed'));
          })
        );
      }
    },
    [attachments.length, maxSizeLabel, t, uploadAcceptedFile, validateFiles]
  );

  const handleAttachmentInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = '';
      await addAttachments(files);
    },
    [addAttachments]
  );

  const handlePaste = useCallback(
    async (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = Array.from(event.clipboardData?.items ?? []);
      const files = items
        .filter((item) => item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file));

      if (!files.length) return;
      event.preventDefault();
      await addAttachments(files);
    },
    [addAttachments]
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const resetAttachments = useCallback(() => {
    setAttachments((prev) => {
      for (const item of prev) {
        if (item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
      return [];
    });
  }, []);

  useEffect(() => {
    return () => {
      for (const item of attachmentsRef.current) {
        if (item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
    };
  }, []);

  const hasPendingUploads = attachments.some(
    (item) => item.uploadStatus !== 'uploaded'
  );

  return {
    addAttachments,
    attachments,
    attachmentsHint,
    attachmentInputRef,
    hasPendingUploads,
    handleAttachmentInputChange,
    handlePaste,
    handleRemoveAttachment,
    resetAttachments,
  };
}
